import { Collection } from '@/types/collection';
import { Grid } from '@/components/layout';
import { LoadingState, ErrorState } from '@/components/composed';
import { Text, Heading, Link, GlobeIcon } from '@/components/atoms';
import { AdaptivePhoto } from '@/components/photos/AdaptivePhoto';
import { collectionEditUrl } from '@/utils/urls';

export interface CollectionGridProps {
  collections?: Collection[];  // T020: Changed from CollectionSummary to Collection to include hero photo data
  isLoading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  className?: string;
}

export function CollectionGrid({
  collections = [],
  isLoading = false,
  error = null,
  onRetry,
  className = '',
}: CollectionGridProps) {
  if (isLoading) {
    return <LoadingState message="Loading collections..." size="lg" />;
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load collections"
        message={error.message || 'An error occurred while loading collections. Please try again.'}
        onRetry={onRetry}
      />
    );
  }

  if (collections.length === 0) {
    return (
      <div className="text-center py-15 px-5 bg-surface-1 rounded-lg my-5">
        <Heading level="h3" className="mb-2">No collections yet</Heading>
        <Text variant="muted" className="mb-4">Create your first collection to showcase your best work!</Text>
      </div>
    );
  }

  return (
    <Grid columns={3} gap={6} className={className}>
      {collections.map((collection) => (
        <div
          key={collection.id}
          className={`relative block bg-surface-1 rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden group ${collection.isOrphaned ? 'opacity-30' : ''}`}
        >
          {/* T020: Hero Image (042-collection-hero-image) */}
          {/* T043: Updated to use AdaptivePhoto for responsive image loading */}
          {/* Feature 653: Hero images are always 3:2 aspect ratio */}
          {collection.heroPhoto ? (
            <Link
              to={collectionEditUrl(collection.globalId || collection.id, collection.slug)}
              className="block aspect-[3/2] overflow-hidden bg-surface-2"
            >
              {collection.heroPhoto.variants && collection.heroPhoto.variants.length > 0 ? (
                <AdaptivePhoto
                  photo={{
                    id: collection.heroPhoto.id,
                    variants: collection.heroPhoto.variants,
                    description: collection.heroPhoto.title
                  }}
                  alt={collection.heroPhoto.title || collection.name}
                  className="w-full h-full"
                />
              ) : (
                <img
                  src={collection.heroPhoto.variants?.[0]?.storageUrl || ""}
                  alt={collection.heroPhoto.title || collection.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              )}
            </Link>
          ) : collection.photoCount > 0 ? (
            <Link
              to={collectionEditUrl(collection.globalId || collection.id, collection.slug)}
              className="block aspect-[3/2] overflow-hidden bg-surface-2 flex items-center justify-center"
            >
              <Text variant="muted" className="text-sm">
                {collection.photoCount} {collection.photoCount === 1 ? 'photo' : 'photos'}
              </Text>
            </Link>
          ) : (
            <Link
              to={collectionEditUrl(collection.globalId || collection.id, collection.slug)}
              className="block aspect-[3/2] bg-surface-2 flex items-center justify-center"
            >
              <Text variant="muted" className="text-sm">No photos</Text>
            </Link>
          )}

          {/* Collection Info */}
          <div className="p-6">
            {/* Shared indicator - globe icon in bottom right */}
            {collection.isShared && (
              <div className="absolute bottom-3 right-3" title="Shared collection">
                <GlobeIcon size={14} className="text-muted opacity-60" />
              </div>
            )}

            <Link
              to={collectionEditUrl(collection.globalId || collection.id, collection.slug)}
              className="no-underline"
            >
              <div className="mb-2">
                <Heading level="h3" className="m-0 mb-1">{collection.name}</Heading>
                <Text variant="muted" className="text-xs">
                  {new Date(collection.createdAt).toLocaleDateString()}
                </Text>
              </div>
              <Text variant="muted" className="text-sm">
                {collection.photoCount} {collection.photoCount === 1 ? 'photo' : 'photos'}
              </Text>
            </Link>
          </div>
        </div>
      ))}
    </Grid>
  );
}
